import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ApiError } from "@/api/client";
import {
  useActivatePromptRevision,
  useDefaultModel,
  useEvalRuns,
  useEvalStatus,
  useEvalSuites,
  useMeasurePromptRevision,
  useConnections,
  usePromptHistory,
  usePromptPreview,
  usePromptState,
  useResetPrompt,
  useSavePrompt,
  type PromptProblem,
  type PromptState,
  type PromptVariantState,
} from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { Field } from "@/components/ui/Field";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { Textarea } from "@/components/ui/Textarea";
import { AlertIcon, CheckIcon, PinIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * The core prompt: edit it, version it, roll it back (PRD FR-2.2).
 *
 * The page is built around one distinction, because everything else follows
 * from it. **Deployment instructions are an addition** — they are appended to
 * the platform prompt, and a deployment that only writes those keeps receiving
 * prompt improvements with every upgrade. **An override replaces** a variant's
 * template, which is allowed and is *pinning*: that variant stops receiving
 * upgrades until it is cleared. So the two live in separate cards, the pinned
 * state is a badge rather than a footnote, and a variant still on the
 * platform's text says so.
 *
 * Both variants are always on screen. M3 shipped a compact prompt with no
 * artifact vocabulary and every local deployment answered in tables for weeks
 * without anyone seeing it; an editor that let you change `full` and forget
 * `compact` existed would be the same mistake with a UI in front of it.
 *
 * Two prompts since MPO-2 — one source, or several — behind a switch, each
 * tab saying how many of its variants are pinned. The server's coupling
 * warning is what says when one shape is overridden and the other is not.
 *
 * **Validation is the server's**, reached through the preview endpoint while
 * the author types. A second implementation of "which placeholders are
 * required" would drift, and the copy that decided whether an answer could be
 * produced at all would be the one nobody was looking at.
 */

type Draft = {
  addendum: string;
  overrides: Record<string, string>;
  multiOverrides: Record<string, string>;
  note: string;
};

/** The two prompts a conversation can be sent (MPO-2): one source, or several. */
type Shape = "single" | "multi";

const VARIANT_LABELS: Record<string, string> = {
  full: "Full",
  compact: "Compact",
  "multi.full": "Full · across sources",
  "multi.compact": "Compact · across sources",
};

const VARIANT_HINTS: Record<string, string> = {
  full: "Models with room for it. Chosen per model in the catalog, not per provider.",
  compact: "Small-window models. Shorter, and it still carries the artifact vocabulary.",
};

function draftOf(state: PromptState): Draft {
  return {
    addendum: state.addendum,
    overrides: { ...state.overrides },
    multiOverrides: { ...(state.multi_overrides ?? {}) },
    note: "",
  };
}

function sameDraft(a: Draft, b: Draft): boolean {
  return (
    a.addendum === b.addendum &&
    JSON.stringify(a.overrides) === JSON.stringify(b.overrides) &&
    JSON.stringify(a.multiOverrides) === JSON.stringify(b.multiOverrides)
  );
}

/** What the save and the preview send: every template, both shapes, at once. */
function bodyOf(draft: Draft, activate: boolean) {
  return {
    addendum: draft.addendum,
    overrides: draft.overrides,
    multi_overrides: draft.multiOverrides,
    note: draft.note,
    activate,
  };
}

// --------------------------------------------------------------------------
// Problems
// --------------------------------------------------------------------------

function Problems({ problems }: { problems: PromptProblem[] }) {
  if (problems.length === 0) return null;
  return (
    <ul className="flex flex-col gap-2">
      {problems.map((problem, index) => {
        const error = problem.severity === "error";
        return (
          <li
            key={`${problem.code}-${index}`}
            className={cn(
              "rounded-container flex items-start gap-2 border p-3",
              error ? "border-error/30 bg-error-subtle/40" : "border-warning/30 bg-warning-subtle/40",
            )}
          >
            <AlertIcon className={cn("mt-0.5 size-4 shrink-0", error ? "text-error" : "text-warning")} />
            <div>
              <p className="type-support text-ink">
                {error ? "Cannot save" : "Worth knowing"} ·{" "}
                <span className="font-mono">{problem.field}</span>
              </p>
              <p className="type-support text-ink-secondary mt-0.5">{problem.message}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// --------------------------------------------------------------------------
// One variant
// --------------------------------------------------------------------------

function VariantPanel({
  variant,
  state,
  platform,
  value,
  editable,
  shape,
  onChange,
}: {
  variant: PromptVariantState;
  state: PromptState;
  platform: string;
  value: string | undefined;
  editable: boolean;
  shape: Shape;
  onChange: (body: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const pinned = value !== undefined && value.trim() !== "";
  const name = VARIANT_LABELS[shape === "multi" ? `multi.${variant.variant}` : variant.variant] ?? variant.variant;

  return (
    <div className="border-border rounded-container border">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="type-label text-ink">{name}</h3>
            {pinned ? (
              <Chip tone="warning" icon={<PinIcon className="size-3" />}>
                Pinned — upgrades won't apply
              </Chip>
            ) : (
              <Chip tone="success">Platform · receives upgrades</Chip>
            )}
          </div>
          <p className="type-support text-ink-secondary mt-1">
            {VARIANT_HINTS[variant.variant] ?? ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="type-support text-ink-disabled">
            {(pinned ? (value ?? "").length : variant.platform_chars).toLocaleString()} chars
          </span>
          {pinned ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={!editable}
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              Use the platform's
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              disabled={!editable}
              onClick={() => {
                onChange(platform);
                setOpen(true);
              }}
            >
              Override…
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setOpen((current) => !current)}>
            {open ? "Hide" : "Show"}
          </Button>
        </div>
      </div>

      {variant.upgrade_available ? (
        <div className="border-warning/30 bg-warning-subtle/40 mx-4 mb-4 flex items-start gap-2 rounded-element border p-3">
          <AlertIcon className="text-warning mt-0.5 size-4 shrink-0" />
          <p className="type-support text-ink-secondary">
            This override was written against prompt {state.active_revision?.base_prompt_version},
            and the platform now ships {state.prompt_version}. Those improvements are not reaching
            this deployment. Compare against the platform text below, or clear the override.
          </p>
        </div>
      ) : null}

      {open ? (
        <div className="px-4 pb-4">
          {pinned ? (
            <Textarea
              label={`${name} template`}
              mono
              rows={18}
              value={value}
              disabled={!editable}
              onChange={(event) => onChange(event.target.value)}
              hint="Placeholders are {{name}}. A literal brace is just a brace — paste JSON examples as they are."
            />
          ) : (
            <pre className="border-border bg-surface rounded-element type-code max-h-96 overflow-auto border p-3 whitespace-pre-wrap">
              {platform}
            </pre>
          )}
        </div>
      ) : null}
    </div>
  );
}

// --------------------------------------------------------------------------
// History
// --------------------------------------------------------------------------

/**
 * Is the live prompt measured? (EVAL-3.) Evidence, never a lock: every
 * banner here offers a run and none of them stops a save or an activation —
 * a deployment with no reachable model must still be able to change its
 * prompt.
 */
function MeasurementBanner() {
  const { data: status } = useEvalStatus();
  const { data: suites } = useEvalSuites();
  const suggested = (suites ?? []).find((suite) => suite.id === status?.suggested_suite_id);
  const measure = useMeasurePromptRevision();
  const { data: defaultModel } = useDefaultModel();
  if (!status) return null;
  const notes: Array<{ tone: "warning" | "error"; text: string }> = [];
  if (status.regression_drop) {
    const drop = status.regression_drop as Record<string, unknown>;
    notes.push({
      tone: "error",
      text: `The weekly regression “${String(drop.suite_name)}” passed ${String(drop.passed)}/${String(drop.cases)}, down from ${String(drop.previous_passed)}.`,
    });
  }
  if (!status.measured) {
    notes.push({
      tone: "warning",
      text:
        status.active_revision === null || status.active_revision === undefined
          ? "The platform prompt this deployment is on has not been measured by an eval run yet."
          : `Unmeasured since revision ${status.active_revision}: no eval run has graded the prompt that is live.`,
    });
  }
  if (status.platform_upgraded) {
    notes.push({
      tone: "warning",
      text: `This build ships prompt ${status.prompt_version}; the last measurement was on ${status.last_measured_prompt_version}. Run the suite to see what the upgrade changed.`,
    });
  }
  if (status.base_behind) {
    notes.push({
      tone: "warning",
      text: `A pinned override was written against ${status.base_prompt_version} and is not receiving ${status.prompt_version}'s improvements. Measure both to decide.`,
    });
  }
  if (!notes.length) {
    return (
      <p className="type-support text-success mb-6 flex items-center gap-2">
        <CheckIcon className="size-4" />
        The live prompt is measured.{" "}
        <Link to="/settings/evals" className="text-accent hover:underline">
          See the run
        </Link>
      </p>
    );
  }
  return (
    <div className="mb-6 flex flex-col gap-2">
      {notes.map((note) => (
        <p
          key={note.text}
          className={cn(
            "type-support rounded-element px-3 py-2",
            note.tone === "error" ? "bg-error-subtle text-error" : "bg-warning-subtle text-warning",
          )}
        >
          {note.text}
        </p>
      ))}
      {status.suggested_suite_id ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            loading={measure.isPending}
            disabled={measure.isSuccess}
            onClick={() =>
              measure.mutate({
                suiteId: status.suggested_suite_id ?? "",
                revision: status.active_revision ?? null,
                modelRef: defaultModel?.ref ?? null,
              })
            }
          >
            {measure.isSuccess ? "Running — see Evals" : "Measure the live prompt"}
          </Button>
          <span className="type-caption text-ink-disabled normal-case">
            Runs the suggested suite against the deployment default model. It spends model tokens.
            {suggested?.multi
              ? ` “${suggested.name}” asks across ${suggested.connection_names.join(" + ")}, so it measures the prompt across sources — its override, if there is one, and the addendum.`
              : ""}
          </span>
        </div>
      ) : (
        <Link to="/settings/evals" className="type-support text-accent hover:underline">
          Make an eval suite to measure it
        </Link>
      )}
    </div>
  );
}

function History({ editable }: { editable: boolean }) {
  const { data, isLoading } = usePromptHistory();
  const activate = useActivatePromptRevision();
  const measure = useMeasurePromptRevision();
  const { data: status } = useEvalStatus();
  const { data: defaultModel } = useDefaultModel();
  const { data: runs } = useEvalRuns(status?.suggested_suite_id ?? null);
  // MPO-3: a revision that changes the prompt across sources is measured by
  // a suite that asks across sources.
  const multiSuiteId =
    status?.suggested_multi_suite_id && status.suggested_multi_suite_id !== status.suggested_suite_id
      ? status.suggested_multi_suite_id
      : null;
  const { data: multiRuns } = useEvalRuns(multiSuiteId);
  const [pending, setPending] = useState<number | null>(null);
  // The newest finished run that measured each revision: the evidence an
  // activation carries into its audit row (EVAL-3).
  const evidence = new Map<number, { id: string; passed: number; cases: number }>();
  const measured = [...(runs ?? []), ...(multiRuns ?? [])].sort((a, b) =>
    b.started_at.localeCompare(a.started_at),
  );
  for (const run of measured) {
    const revision = run.prompt_candidate ?? run.prompt_revision;
    if (run.status === "complete" && typeof revision === "number" && !evidence.has(revision)) {
      evidence.set(revision, { id: run.id, passed: run.passed, cases: run.cases });
    }
  }

  if (isLoading) return <Spinner />;
  const rows = data ?? [];
  if (rows.length === 0) {
    return (
      <p className="type-support text-ink-secondary">
        Nothing saved yet — this deployment is on the platform prompt.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <li
          key={row.revision}
          className={cn(
            "rounded-container flex flex-wrap items-center justify-between gap-3 border p-3",
            row.active ? "border-accent bg-accent-subtle/30" : "border-border",
          )}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="type-label text-ink">Revision {row.revision}</span>
              {row.active ? (
                <Chip tone="accent" icon={<CheckIcon className="size-3" />}>
                  Live
                </Chip>
              ) : null}
              {row.pinned.length > 0 || (row.pinned_multi ?? []).length > 0 ? (
                <Chip tone="warning" icon={<PinIcon className="size-3" />}>
                  {[
                    ...row.pinned.map((v) => VARIANT_LABELS[v] ?? v),
                    ...(row.pinned_multi ?? []).map((v) => VARIANT_LABELS[`multi.${v}`] ?? v),
                  ].join(", ")}{" "}
                  overridden
                </Chip>
              ) : null}
            </div>
            <p className="type-support text-ink-secondary mt-1 truncate">
              {row.note || "No note"} · {new Date(row.created_at).toLocaleString()}
              {row.visitor_id ? ` · visitor ${row.visitor_id.slice(0, 8)}` : ""} · written against{" "}
              {row.base_prompt_version}
            </p>
          </div>
          {row.active ? null : (
            <div className="flex flex-wrap items-center gap-2">
              {status?.suggested_suite_id ? (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={measure.isPending && pending === -row.revision}
                  title="Run the suggested eval suite under this revision, without making it live. Spends model tokens."
                  onClick={() => {
                    setPending(-row.revision);
                    measure.mutate({
                      suiteId: status.suggested_suite_id ?? "",
                      revision: row.revision,
                      modelRef: defaultModel?.ref ?? null,
                    });
                  }}
                >
                  Measure
                </Button>
              ) : null}
              {(row.pinned_multi ?? []).length > 0 && multiSuiteId ? (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={measure.isPending && pending === -row.revision - 0.5}
                  title="This revision changes the prompt across sources: run a suite that asks across sources under it, without making it live. Spends model tokens."
                  onClick={() => {
                    setPending(-row.revision - 0.5);
                    measure.mutate({
                      suiteId: multiSuiteId,
                      revision: row.revision,
                      modelRef: defaultModel?.ref ?? null,
                    });
                  }}
                >
                  Measure across sources
                </Button>
              ) : null}
              <Button
                variant="secondary"
                size="sm"
                disabled={!editable}
                loading={activate.isPending && pending === row.revision}
                onClick={() => {
                  setPending(row.revision);
                  activate.mutate({
                    revision: row.revision,
                    evalRunId: evidence.get(row.revision)?.id ?? null,
                  });
                }}
              >
                {evidence.get(row.revision)
                  ? `Activate · measured ${evidence.get(row.revision)?.passed}/${evidence.get(row.revision)?.cases}`
                  : "Activate"}
              </Button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

// --------------------------------------------------------------------------

export function PromptsPage() {
  const { data: state, isLoading } = usePromptState();
  const { data: connections } = useConnections();
  const save = useSavePrompt();
  const reset = useResetPrompt();
  const preview = usePromptPreview();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [connectionId, setConnectionId] = useState("");
  const [showPreview, setShowPreview] = useState<string | null>(null);
  const [shape, setShape] = useState<Shape>("single");
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    if (state && draft === null) setDraft(draftOf(state));
  }, [state, draft]);

  const base = useMemo(() => (state ? draftOf(state) : null), [state]);
  const dirty = draft !== null && base !== null && !sameDraft(draft, base);

  // The server validates, debounced, while the author types. It is the same
  // check the save runs — there is no second copy of the rules in here.
  const previewMutate = preview.mutate;
  useEffect(() => {
    if (!draft) return;
    const timer = setTimeout(() => {
      previewMutate({
        body: { ...bodyOf(draft, true), note: "" },
        connectionId: connectionId || undefined,
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [draft, connectionId, previewMutate]);

  if (isLoading || !state || !draft) {
    return (
      <div className="flex h-full items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const problems = preview.data?.problems ?? [];
  const blocked = problems.some((problem) => problem.severity === "error");
  const error = save.error instanceof ApiError ? save.error.message : null;
  const editable = state.editable;

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-8 sm:py-10">
      <MeasurementBanner />
      <header className="mb-8">
        <p className="type-caption text-ink-disabled">Admin</p>
        <h1 className="type-h1 text-ink mt-2">Prompt</h1>
        <p className="type-body text-ink-secondary mt-3 max-w-2xl">
          What the assistant is told before it sees a question. Instructions you add here reach
          every source; a source's own notes and instructions are edited on its page and are
          appended after these.
        </p>
        <p className="type-support text-ink-secondary mt-3">
          This build ships prompt <span className="text-ink font-mono">{state.prompt_version}</span>
          .{" "}
          {state.revision === null
            ? "This deployment has changed nothing, so prompt improvements arrive with every upgrade."
            : `This deployment is on revision ${state.revision}.`}
        </p>
      </header>

      {!editable ? (
        <div className="border-warning/30 bg-warning-subtle/40 rounded-container mb-6 flex items-start gap-2 border p-4">
          <AlertIcon className="text-warning mt-0.5 size-4 shrink-0" />
          <p className="type-support text-ink-secondary">
            This deployment's prompt is owned by the platform (
            <span className="font-mono">MING_PROMPT_ADMIN_ENABLED=false</span>). You can read it
            here; saving is refused rather than silently ignored.
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-5 pb-4">
        <Card>
          <CardHeader
            title="Deployment instructions"
            description="Appended to the platform prompt — an addition, so upgrades still reach you"
          />
          <CardBody className="flex flex-col gap-4">
            <Textarea
              label="Instructions for every source"
              rows={6}
              value={draft.addendum}
              disabled={!editable}
              placeholder="House style, vocabulary, or a standing caveat that is true of this whole deployment."
              onChange={(event) =>
                setDraft({ ...draft, addendum: event.target.value })
              }
              hint={`Plain text, not a template. Up to ${state.max_addendum_chars.toLocaleString()} characters. Knowledge about one source belongs in that source's data dictionary, where it only costs context when that source is being used.`}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Core prompt"
            description="Two prompts — one source, or several — each with two variants chosen per model in the catalog. Overriding one pins it, and only it."
          />
          <CardBody className="flex flex-col gap-3">
            {/* MPO-2: a conversation is sent one prompt or the other, so both
                are edited here, as equals. */}
            <div role="group" aria-label="Which prompt" className="flex flex-wrap gap-2">
              {(["single", "multi"] as const).map((option) => {
                const count = Object.keys(
                  option === "single" ? draft.overrides : draft.multiOverrides,
                ).length;
                return (
                  <Button
                    key={option}
                    size="sm"
                    variant={shape === option ? "subtle" : "ghost"}
                    aria-pressed={shape === option}
                    onClick={() => setShape(option)}
                  >
                    {option === "single" ? "Single source" : "Across sources"}
                    {count ? ` · ${count} pinned` : ""}
                  </Button>
                );
              })}
            </div>
            <p className="type-support text-ink-secondary">
              {shape === "single"
                ? "Sent when a conversation uses one source."
                : "Sent when a conversation uses more than one source: the source roster, the cross-source protocol and the declared join keys. Deployment instructions are appended to it too."}
            </p>
            {(shape === "single" ? state.variants : state.multi_variants ?? []).map((variant) => (
              <VariantPanel
                key={`${shape}-${variant.variant}`}
                variant={variant}
                state={state}
                shape={shape}
                platform={
                  (shape === "single" ? state.platform : state.multi_source_platform ?? {})[
                    variant.variant
                  ] ?? ""
                }
                value={(shape === "single" ? draft.overrides : draft.multiOverrides)[variant.variant]}
                editable={editable}
                onChange={(body) => {
                  const key = shape === "single" ? "overrides" : "multiOverrides";
                  const next = { ...draft[key] };
                  if (body === undefined) delete next[variant.variant];
                  else next[variant.variant] = body;
                  setDraft({ ...draft, [key]: next });
                }}
              />
            ))}
            {shape === "single" ? (
              <p className="type-support text-ink-secondary">
                Known placeholders:{" "}
                {Object.keys(state.placeholders)
                  .sort()
                  .map((name) => `{{${name}}}`)
                  .join(", ")}
                . Required: {state.required_placeholders.map((name) => `{{${name}}}`).join(", ")} —
                without them a source's notes, this deployment's instructions, the engine's dialect
                or today's date stop reaching the model.
              </p>
            ) : (
              <p className="type-support text-ink-secondary">
                Known placeholders across sources:{" "}
                {Object.keys(state.multi_placeholders ?? {})
                  .sort()
                  .map((name) => `{{${name}}}`)
                  .join(", ")}
                . Required:{" "}
                {(state.multi_required_placeholders ?? []).map((name) => `{{${name}}}`).join(", ")} —
                without them the source roster, each source's notes, the declared join keys, this
                deployment's instructions or today's date stop reaching the model.
              </p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Check"
            description="The same validation the save runs, and exactly what a model would be sent"
          />
          <CardBody className="flex flex-col gap-4">
            <Select
              label="Preview against"
              value={connectionId}
              onChange={(event) => setConnectionId(event.target.value)}
              hint="A real source fills in its name, engine and data dictionary."
            >
              <option value="">An example source</option>
              {(connections ?? []).map((connection) => (
                <option key={connection.id} value={connection.id}>
                  {connection.name}
                </option>
              ))}
            </Select>

            {preview.isPending ? (
              <p className="type-support text-ink-secondary">Checking…</p>
            ) : problems.length === 0 ? (
              <p className="type-support text-success flex items-center gap-2">
                <CheckIcon className="size-4" /> Nothing to flag.
              </p>
            ) : (
              <Problems problems={problems} />
            )}

            {preview.data ? (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                  {Object.keys(preview.data.rendered).map((variant) => (
                    <Button
                      key={variant}
                      variant={showPreview === variant ? "subtle" : "ghost"}
                      size="sm"
                      onClick={() =>
                        setShowPreview((current) => (current === variant ? null : variant))
                      }
                    >
                      {VARIANT_LABELS[variant] ?? variant} ·{" "}
                      {(preview.data?.chars[variant] ?? 0).toLocaleString()} chars
                    </Button>
                  ))}
                </div>
                {showPreview ? (
                  <pre className="border-border bg-surface rounded-element type-code max-h-96 overflow-auto border p-3 whitespace-pre-wrap">
                    {preview.data.rendered[showPreview]}
                  </pre>
                ) : null}
              </div>
            ) : null}
          </CardBody>
        </Card>

        {error ? (
          <div className="border-error/30 bg-error-subtle/40 rounded-container flex items-start gap-2 border p-4">
            <AlertIcon className="text-error mt-0.5 size-4 shrink-0" />
            <p className="type-support text-ink-secondary">{error}</p>
          </div>
        ) : null}

        <div className="border-border flex flex-wrap items-end justify-between gap-3 border-t pt-4">
          <Field
            label="What changed"
            className="min-w-64"
            value={draft.note}
            disabled={!editable}
            placeholder="Why this revision exists"
            onChange={(event) => setDraft({ ...draft, note: event.target.value })}
            hint="Written to the history beside the revision. The sentence a rollback is decided on."
          />
          <div className="flex items-center gap-2">
            {confirmReset ? (
              <>
                <span className="type-support text-ink-secondary">
                  Back to the platform prompt?
                </span>
                <Button
                  loading={reset.isPending}
                  onClick={() =>
                    reset.mutate(undefined, {
                      onSuccess: (next) => {
                        setConfirmReset(false);
                        setDraft(draftOf(next));
                      },
                    })
                  }
                >
                  Reset
                </Button>
                <Button variant="ghost" onClick={() => setConfirmReset(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                disabled={!editable || state.revision === null}
                onClick={() => setConfirmReset(true)}
              >
                Reset to platform
              </Button>
            )}
            {/* EVAL-3: save it, measure it, then activate it with the evidence. */}
            <Button
              disabled={!editable || !dirty || blocked}
              loading={save.isPending && save.variables?.activate === false}
              title="Store this revision without making it live, so it can be measured first."
              onClick={() =>
                save.mutate(bodyOf(draft, false), { onSuccess: (next) => setDraft(draftOf(next)) })
              }
            >
              Save without activating
            </Button>
            <Button
              variant="primary"
              disabled={!editable || !dirty || blocked}
              loading={save.isPending && save.variables?.activate !== false}
              onClick={() =>
                save.mutate(bodyOf(draft, true), { onSuccess: (next) => setDraft(draftOf(next)) })
              }
            >
              Save as a new revision
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader
            title="History"
            description="Every prompt this deployment has run. Answers record which one produced them."
          />
          <CardBody>
            <History editable={editable} />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
